import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { EXPERIENCE_CATEGORIES, EXPERIENCE_IMAGES, EXPERIENCE_LAST_CHECKED } from "@/data/experiences";
import { BESPOKE_COPY, EXPERIENCE_TOURS, GOOGLE_FORM_URL, TOUR_ESTIMATE_NOTE } from "@/data/content";
import { Price } from "@/lib/currency";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { EnquiryModal } from "@/components/EnquiryModal";
import { FadeUp, SectionHeading } from "@/components/Reveal";

const BespokeCta = ({ testid }) => (
  <div className="relative overflow-hidden bg-jawa-espresso p-10 text-center lg:p-14" data-testid={testid}>
    <div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-jawa-coral/20 blur-2xl" />
    <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-jawa-turquoise/15 blur-2xl" />
    <h3 className="font-serif text-3xl font-medium text-jawa-ivory">Let's curate a bespoke experience for you.</h3>
    <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-jawa-ivory/70">{BESPOKE_COPY}</p>
    <a
      href={GOOGLE_FORM_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`${testid}-button`}
      className="mt-8 inline-flex items-center gap-2 bg-jawa-coral px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-turquoise hover:text-jawa-nearblack"
    >
      Plan My Experience <ArrowUpRight className="h-4 w-4" />
    </a>
  </div>
);

export { BespokeCta };

const TourCard = ({ tour, onBook, index }) => (
  <FadeUp delay={(index % 3) * 0.05} className="h-full">
    <article
      id={`tour-${tour.id}`}
      data-testid={`tour-${tour.id}`}
      className="flex h-full scroll-mt-40 flex-col overflow-hidden border border-jawa-stone bg-white transition-colors hover:border-jawa-turquoise/60"
    >
      <div className="relative h-44 overflow-hidden">
        <img src={tour.image} alt={tour.imgAlt || tour.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        <span className="absolute left-4 top-4 bg-jawa-nearblack/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-jawa-gold">
          {tour.duration}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-jawa-espresso/50">
          <MapPin className="h-3 w-3" /> {tour.destination}
        </p>
        <h3 className="mt-1.5 font-serif text-xl font-medium text-jawa-espresso">{tour.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-jawa-espresso/65">{tour.blurb}</p>
        <p className="mt-3 text-[11px] leading-relaxed text-jawa-espresso/50">
          <span className="font-semibold text-jawa-espresso/70">Includes:</span> {tour.includes}.{" "}
          <span className="font-semibold text-jawa-espresso/70">Not included:</span> {tour.excludes}.
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-jawa-stone pt-4">
          <span data-testid={`tour-price-${tour.id}`}>
            <span className="font-serif text-2xl font-medium text-jawa-espresso">
              {tour.pricePrefix || ""}
              <Price amount={tour.price} from="USD" />
            </span>
            <span className="block text-[10px] uppercase tracking-[0.15em] text-jawa-espresso/50">
              {tour.basis === "group" ? `per group (up to ${tour.groupSize})` : "per person"}
            </span>
          </span>
          <button
            onClick={() => onBook(tour)}
            data-testid={`tour-book-${tour.id}`}
            className="inline-flex items-center gap-1.5 bg-jawa-espresso px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
          >
            Request booking <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </article>
  </FadeUp>
);

export default function ExperiencesPage() {
  const [active, setActive] = useState(EXPERIENCE_CATEGORIES[0].id);
  const [enquiring, setEnquiring] = useState(null);
  const { hash } = useLocation();
  const cat = EXPERIENCE_CATEGORIES.find((c) => c.id === active);

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <div className="bg-jawa-ivory" data-testid="experiences-page">
      <Nav />
      <Seo
        title="Tours & Experiences — Guided Lagos Days, Dining, Culture"
        description="Bookable guided tours and curated Lagos recommendations across dining and rooftops, art and culture, beach clubs, nightlife, wellness, day trips and more."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline="Tours & experiences" title="Nigeria, curated." />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
            Bookable guided tours below — plus hand-picked recommendations across dining, culture, beaches,
            nightlife and more, genuine places we would send a friend. Curated {EXPERIENCE_LAST_CHECKED};
            listings are reviewed periodically — tell the concierge if anything has changed.
          </p>
        </FadeUp>

        <FadeUp className="mt-16">
          <SectionHeading overline="Bookable tours" title="Guided days, hosted by people we trust." />
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-jawa-espresso/60">{TOUR_ESTIMATE_NOTE}</p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="tours-grid">
            {EXPERIENCE_TOURS.map((t, i) => (
              <TourCard key={t.id} tour={t} index={i} onBook={setEnquiring} />
            ))}
          </div>
        </FadeUp>

        <FadeUp className="mt-24">
          <SectionHeading overline="Browse by experience" title="Places we'd send a friend." />
        </FadeUp>

        <div className="sticky top-[72px] z-30 mt-10 border-y border-jawa-stone bg-jawa-ivory/95 py-4 backdrop-blur-md" data-testid="experience-category-bar">
          <div className="flex flex-wrap gap-2">
            {EXPERIENCE_CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`experience-tab-${c.id}`}
                className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
                  active === c.id
                    ? "bg-jawa-coral text-white"
                    : "border border-jawa-espresso/20 text-jawa-espresso/60 hover:border-jawa-turquoise hover:text-jawa-espresso"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <FadeUp key={cat.id} className="mt-10">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">{cat.name}</h2>
            <p className="text-xs uppercase tracking-[0.25em] text-jawa-turquoise">{cat.tag} · {cat.items.length} picks</p>
          </div>
          {EXPERIENCE_IMAGES[cat.id] && (
            <div className="mt-6 overflow-hidden" data-testid={`experience-banner-${cat.id}`}>
              <motion.img
                key={cat.id}
                src={EXPERIENCE_IMAGES[cat.id]}
                alt={cat.name}
                loading="lazy"
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="h-64 w-full object-cover md:h-96"
              />
            </div>
          )}
          <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid={`experience-list-${cat.id}`}>
            {cat.items.map((it) => (
              <article
                key={it.name}
                className="flex flex-col border border-jawa-stone bg-white p-6 transition-colors hover:border-jawa-coral/60"
                data-testid={`experience-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-xl font-medium text-jawa-espresso">{it.name}</h3>
                    <p className="mt-1 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-jawa-espresso/50">
                      <MapPin className="h-3 w-3" /> {it.area} · {it.kind}
                    </p>
                  </div>
                  {it.price && (
                    <span className="shrink-0 border border-jawa-gold/50 px-2 py-0.5 text-xs font-semibold text-jawa-espresso/70">
                      {it.price}
                    </span>
                  )}
                </div>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-jawa-espresso/70">{it.why}</p>
                {it.link && (
                  <a
                    href={it.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`experience-link-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-4 inline-flex w-fit items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
                  >
                    {it.link.startsWith("/") ? "Open the guide" : "Visit official site"} <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                )}
              </article>
            ))}
          </div>
        </FadeUp>

        <FadeUp className="mt-16">
          <BespokeCta testid="experiences-bespoke-cta" />
        </FadeUp>
      </main>
      <Footer />
      {enquiring && (
        <EnquiryModal
          journey="Tours & Experiences"
          title={enquiring.title}
          variant="tour"
          tour={enquiring}
          priceNote={`${enquiring.pricePrefix || ""}$${enquiring.price} ${
            enquiring.basis === "group" ? `per group (up to ${enquiring.groupSize})` : "per person"
          } — launch estimate, confirmed before payment.`}
          onClose={() => setEnquiring(null)}
          testid="tour-enquiry-modal"
        />
      )}
    </div>
  );
}
